import { Component, computed, DestroyRef, effect, inject, input, linkedSignal, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { distinctUntilChanged } from 'rxjs';

import { ReferentielStore } from '../../administration/gestion-référentiels/store/referentiel.store';
import { FormControlNames } from '../../référentiel-métier-parcours/models/form-control-names.model';
import { ParcoursStore } from '../../référentiel-métier-parcours/store/parcours.store';

import { AlerteComponent } from '../../shared/components/alerte/alerte.component';
import { SidepanelService } from '../../shared/components/sidepanel/sidepanel.service';
import { SERVICE_INSTITUTS_METIER } from '../../shared/models/service-instituts-métier';
import { TrierParPipe } from '../../shared/pipes/trier-par.pipe';
import { FiltresCatalogueNational } from '../catalogue-national.component';
import { REFERENTIEL_TYPE } from '../../administration/gestion-référentiels/models/référentiel-type.enum';

@Component({
  selector: 'app-filtres-catalogue-national',
  imports: [
    HlmSelectImports,
    TrierParPipe,
    BrnSelectImports,
    ReactiveFormsModule,
    HlmFieldImports,
    HlmButtonImports,
    AlerteComponent,
  ],
  templateUrl: './filtres-catalogue-national.component.html',
})
export class FiltresCatalogueNationalComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #referentielStore = inject(ReferentielStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #router = inject(Router);
  readonly #parcoursStore = inject(ParcoursStore);

  readonly idCartographie = input.required<string>();
  readonly filtres = input<FiltresCatalogueNational>();

  readonly equipesResponsables = SERVICE_INSTITUTS_METIER.flatMap((x) => x.items);
  form: FormGroup = this.#fb.group({
    [FormControlNames.INSTITUT]: [''],
    [FormControlNames.METIER]: [''],
    [FormControlNames.PUBLICS_CIBLES]: [[]],
  });

  readonly referentiels = computed(() => this.#referentielStore.referentielsActifs());
  readonly referentielsFiltres = linkedSignal(() => this.referentiels());
  estMetier = signal<boolean>(false);
  readonly publicsCibles = computed(() =>
    [
      ...this.#parcoursStore
        .entities()
        .flatMap((x) => x.noeuds.filter((x) => x.estPointDepart))
        .map((x) => x.nom),
      'Poursuite de parcours',
    ].sort(),
  );

  ngOnInit(): void {
    const institutsMetier = this.equipesResponsables
      .filter((x) => x.type === REFERENTIEL_TYPE.METIER)
      .map((x) => x.href);
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());

    this.institut.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef), distinctUntilChanged()).subscribe(() => {
      this.estMetier.set(institutsMetier.includes(this.institut.value));
      this.referentielsFiltres.set(this.referentiels().filter((x) => x.equipeResponsable === this.institut.value));
    });
  }

  constructor() {
    effect(() => {
      const filtres = this.filtres();
      if (filtres) {
        this.form.setValue({
          [FormControlNames.INSTITUT]: filtres.institut ?? null,
          [FormControlNames.METIER]: filtres.metier ?? null,
          [FormControlNames.PUBLICS_CIBLES]: filtres.publicsCibles ?? [],
        });
      }
    });
  }

  onSubmit() {
    const idMetier = this.estMetier() && this.referentielsFiltres().length > 0 ? this.metier.value : null;
    this.#navigateWithPathParameter(idMetier, this.institut.value, this.publicsCiblesFiltre.value);
  }

  #navigateWithPathParameter(metier: string | null, institut: string | null, publicsCibles: string[] | null) {
    this.#router.navigate([], {
      queryParams: {
        institut: institut,
        metier: metier,
        publicsCibles: publicsCibles,
      },
    });
  }

  get institut() {
    return this.form.get(FormControlNames.INSTITUT) as AbstractControl;
  }

  get metier() {
    return this.form.get(FormControlNames.METIER) as AbstractControl;
  }

  get publicsCiblesFiltre() {
    return this.form.get(FormControlNames.PUBLICS_CIBLES) as FormControl<string[]>;
  }
}
